import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import url from "web/script/utils/url";

const DEEP_LINK_PAGE_TYPES = new Set(["product"]);
const DEEP_LINK_PAGE_SUB_TYPES = new Set([
    "designer",
    "generic_category",
    "generic_subcategory",
    "designer_category",
    "designer_sub_category",
]);

const ANALYTICS_CATEGORY_WHITELIST = new Set([
    "filter",
    "feeds_show_more",
    "feed",
    "sort_by",
    "nav_search",
    "product_card",
    "save_for_later",
    // The EmailCapture overlay is opened...
    "signup_pdp_save",
    "signup_lead_save",
    "signup_feed_save",
    "signup_du",
    "signup_search",
    "stock_alert",
    "stock_alert_lead_return",
    "signup_oos_save",
    // Signed up
    "signup_header",
    // Tracking
    "affiliate",
    // icon
    "cart",
    "buy_area",
    "get_updates_designer",
]);

export enum RawBranchLink {
    DesignerUpdates = "https://lyst.app.link/KpHF8KyBZ3",
    SaveForLater = "https://lyst.app.link/4fHObAlxZ3",
    Footer = "https://lyst.app.link/9Q09TGy4U5",
    AppPage = "https://lyst.app.link/84mTCgEAd6",
    AppTeaser = "https://lyst.app.link/kBbgC3bBd6",
    ShowMore = "https://lyst.app.link/KJLyM3c0Q6",
    ComparePrices = "https://lyst.app.link/bK2Ohz8WX6",
}

export enum CampaignRawBranchLink {
    LeadReturn = "https://lyst.app.link/1xX3NV4Xp7",
    ISPDP = "https://lyst.app.link/2xX3NV4Xp7",
    OOSPDP = "https://lyst.app.link/4xX3NV4Xp7",
    Feed = "https://lyst.app.link/3xX3NV4Xp7",
    SignedInUsers = "https://lyst.app.link/5xX3NV4Xp7",
    Homepage = "https://lyst.app.link/6xX3NV4Xp7",
}

export interface BranchIOLinkData {
    web_session_id: string;
    page_type: string; // one of ?
    page_sub_type: string;
    web_device_id: string;
    web_pageview_id: string;
    user_id?: string;
    $deeplink_path?: string;
}

export interface BranchIOMetaData {
    trafficSource: string;
    lifeCycleStage: string;
    isReturningUser: boolean;
    country: string;
    domain: string;
    page_sub_type: string;
    page_type: string;
    initial_page_sub_type: string;
    initial_page_type: string;
    user_id?: string;
}

export interface SimpleAnalyticsEvent {
    type: string;
    data: {
        category: string;
        action: string;
        label?: string;
        life_cycle_stage: string;
        returning_user: boolean;
        country: string;
        domain: string;
        page_sub_type: string;
        page_type: string;
        initial_page_sub_type: string;
        initial_page_type: string;
        user_id: number | null;
    };
}

function getTrafficSource(): "paid" | "other" {
    let params = url.parse(globals.window.location.href).searchParams;

    return params.get("atc_medium") == "cpc" || params.get("utm_medium") == "cpc"
        ? "paid"
        : "other";
}

export function getLinkData(): BranchIOLinkData {
    const pageType = environment.get("pageType");
    const pageSubType = environment.get("pageSubType");
    let branchIOLinkData: BranchIOLinkData = {
        web_session_id: analytics.getSessionId(),
        page_type: pageType,
        page_sub_type: pageSubType,
        web_device_id: analytics.getDeviceUid(),
        web_pageview_id: analytics.getPageViewId(),
    };
    const userId = environment.get("userId");
    if (userId) {
        branchIOLinkData.user_id = userId;
    }

    if (DEEP_LINK_PAGE_TYPES.has(pageType) || DEEP_LINK_PAGE_SUB_TYPES.has(pageSubType)) {
        branchIOLinkData["$deeplink_path"] = globals.window.location.pathname;
    }

    return branchIOLinkData;
}

export function getBranchMetadata(event: SimpleAnalyticsEvent): BranchIOMetaData {
    let branchMetaData: BranchIOMetaData = {
        trafficSource: getTrafficSource(),
        lifeCycleStage: event.data.life_cycle_stage,
        isReturningUser: event.data.returning_user,
        country: event.data.country,
        domain: event.data.domain,
        page_sub_type: event.data.page_sub_type,
        page_type: event.data.page_type,
        initial_page_sub_type: event.data.initial_page_sub_type,
        initial_page_type: event.data.initial_page_type,
    };

    if (event.data.user_id) {
        branchMetaData.user_id = event.data.user_id.toString();
    }

    return branchMetaData;
}

export function getBranchAnalyticsName(event: SimpleAnalyticsEvent): string {
    // Do not append label as it makes targeting too granular in branch.
    let branchIOEventName = `${event.data.category}_${event.data.action}`;
    return branchIOEventName;
}

export function shouldSendAnalytics(event: SimpleAnalyticsEvent): boolean {
    return event.type === "event" && ANALYTICS_CATEGORY_WHITELIST.has(event.data.category);
}
